import React, { useState, useEffect } from 'react'
import { Redirect } from 'react-router'
import { ApolloError } from 'apollo-client'
import { useKey, useSessionStorage, useLocalStorage } from 'react-use'
import * as Sentry from '@sentry/browser'

// Constants
import { routes, IS_EMBEDDED } from '@sketch/constants'

// Components
import { ZoomTypes } from 'components/Zoom'
import { CenterContent } from 'containers/CenterContent'
import { useForTablet, IsEmbedded, NotEmbedded } from '@sketch/components'
import { useToast } from '@sketch/toasts'
import DocumentSidebarLayout, {
  DocumentSidebarLayoutHeaderProps,
} from 'containers/DocumentSidebarLayout'
import DetailHeader from 'modules/shares/components/DetailHeader'
import { EmbeddedDetailHeader } from 'modules/shares/components/EmbeddedDetailHeader'
import ArtboardView from 'modules/shares/components/ArtboardView'
import { CustomFontsMessage } from 'modules/shares/components/CustomFontsMessage'

// Local Components
import DetailSidebar from '../components/DetailSidebar'
import { EmbeddedErrorPage } from '../components/EmbeddedErrorPage'
import ShareWorkspaceSidebar from '../components/ShareWorkspaceSidebar'
import { useGetActiveSidebarTab } from '../components/ShareSidebarTabContext'

// Styles
import { PageContent } from './DetailView.styles'

// Types
import { LayerId, Pagination } from '../types'
import {
  ArtboardDetailInfoFragment,
  ShareInfoFragment,
  VersionFragment,
} from '@sketch/gql-types'
import { InspectorError } from 'modules/shares/Inspector/types'

// Hooks
import useGetInspectorData from './useGetInspectorData'
import { useSymbol } from '../Inspector/Symbol'
import useUserProfile from 'modules/user/useUserProfile'

// Context
import { ArtboardContext, defaultLayoutSettings } from './ArtboardContext'
import { ComponentDescriptionContextProvider } from '../ComponentsWebView/context'

// Utils
import { hasGraphQLErrorCode } from 'graphql/utils/graphqlErrors'
import { ErrorHandler } from '@sketch/tracing'
import Inspector from '../Inspector/Sidebar/components/Inspector'
import DocumentMobileToolbar, {
  isMobileToolboxItem,
} from '../components/DocumentMobileToolbar'
import { usePlaceDraftAnnotation } from 'modules/annotations/hooks'

/**
 * TYPES
 */
export interface DetailViewProps {
  share: OmitSafe<ShareInfoFragment, 'version'>
  currentVersion: VersionFragment | undefined
  artboard: ArtboardDetailInfoFragment | undefined
  isViewingLatestVersion: boolean
  isPrototypePlayEnabled: boolean
  pagination: Pagination | undefined
  artboardIdAtLatestVersion: string | undefined
  error: ApolloError | undefined
  loading: boolean
}

/**
 * COMPONENT
 */
export const DetailView = ({
  share,
  currentVersion,
  artboard,
  isViewingLatestVersion,
  isPrototypePlayEnabled,
  pagination,
  artboardIdAtLatestVersion,
  error,
  loading,
}: DetailViewProps) => {
  const isTabletAndBigger = useForTablet()
  const activeSidebarTab = useGetActiveSidebarTab()

  // Annotations hooks

  const [placeDraftAnnotation, setPlacingDraftAnnotation] =
    usePlaceDraftAnnotation() || []

  // Inspector Internal state
  const [hoveredLayerId, setHoveredLayerId] = useState<LayerId | undefined>()
  const [selectedLayerId, setSelectedLayerId] = useState<LayerId | undefined>()
  const [isGridEnabled, setIsGridEnabled] = useLocalStorage(
    'inspector_grid_enabled',
    false
  )
  const [isLayoutEnabled, setIsLayoutEnabled] = useLocalStorage(
    'inspector_layout_enabled',
    false
  )
  const [zoom, setZoom] = useSessionStorage<ZoomTypes>('artboard-zoom', {
    type: 'ZOOM_TO_FIT',
  })
  const [inspectorActive, setInspectorActive] = useSessionStorage<boolean>(
    'inspector-active',
    false
  )

  const isInspectorEnabled = activeSidebarTab === 'Inspect'
  const { data: userData } = useUserProfile()

  // Manage inspector data
  const artboardId = artboard?.identifier
  const [
    loadInspectorData,
    { inspectorData, inspectorError, inspectorErrorReason },
  ] = useGetInspectorData({
    artboard,
  })

  const inspectorDataErrors = inspectorData?.errors
    ? inspectorData.errors
        .map((error: InspectorError) => error.extensions.code)
        .join(', ')
    : null
  const inspectorDataErrorReasons = inspectorData?.errors
    ? inspectorData.errors
        .map((error: InspectorError) => error.extensions.reason)
        .join(', ')
    : null

  useEffect(() => {
    if (userData?.me?.userPreferences?.showGrid) {
      setIsGridEnabled(!!userData?.me?.userPreferences?.showGrid)
    }
  }, [userData, setIsGridEnabled])

  // Reset selected layers when the artboard change
  useEffect(() => {
    setSelectedLayerId(undefined)
    setHoveredLayerId(undefined)
  }, [artboardId])

  // Symbol data needed for both canvas (ArtboardView) and sidebar (DetailSidebar)
  const {
    symbolLayer,
    symbolData,
    symbolArtboard,
    foreignSymbolShareId,
    foreignVersionShortId,
    foreignSymbolShareName,
    foreignSymbolShareError,
  } = useSymbol({
    inspectorData,
    selectedLayerId,
    currentVersion,
  })

  useEffect(() => {
    // we want to load the inspector IF we have an artboard, the tab is selected or we need to load the grid or layout
    if (
      artboard &&
      artboardId &&
      (isInspectorEnabled || isGridEnabled || isLayoutEnabled)
    ) {
      loadInspectorData()
    }
  }, [
    artboard,
    loadInspectorData,
    inspectorActive,
    artboardId,
    setInspectorActive,
    isGridEnabled,
    isLayoutEnabled,
    isInspectorEnabled,
  ])

  // Inspector data object needed for both canvas (ArtboardView) and sidebar (DetailSidebar)
  const inspector = {
    selectedLayerId,
    hoveredLayerId,
    data: inspectorData,
    isActive: isInspectorEnabled,
    onClickLayer: setSelectedLayerId,
    onHoverLayer: setHoveredLayerId,
    symbol: {
      symbolLayer,
      symbolData,
      symbolArtboard: symbolArtboard
        ? {
            ...symbolArtboard,
            // TODO: mark id fields of Artboard as non nullable
            // https://github.com/sketch-hq/Cloud/issues/4679
            permanentArtboardShortId: symbolArtboard.permanentArtboardShortId!,
          }
        : null,
      foreignSymbolShareId,
      foreignVersionShortId,
      foreignSymbolShareName,
      foreignSymbolShareError,
    },
  }

  // keyboard shortcuts
  useKey(
    'Escape',
    () => {
      if (isInspectorEnabled && selectedLayerId) {
        setSelectedLayerId(undefined)
      }
    },
    {},
    [selectedLayerId]
  )

  const { showToast } = useToast()
  useEffect(() => {
    if (error && !IS_EMBEDDED) {
      if (hasGraphQLErrorCode(error, 'NOT_FOUND')) {
        showToast('This artboard does not exist.', 'negative')
      } else if (!error.networkError) {
        showToast(
          'Oops, something went wrong while loading the artboard.',
          'negative'
        )
        Sentry.captureException(error)
        ErrorHandler.shouldNeverHappen(
          `Something went wrong while fetching getDetailViewArtboard query: '${error.message}'`
        )
      }
    }
  }, [error, showToast])

  const shouldRenderSidebarRight =
    !error && !IS_EMBEDDED && !!artboard && !!share && !!currentVersion

  const toggleGrid = () => setIsGridEnabled(!isGridEnabled)
  const toggleLayout = () => setIsLayoutEnabled(!isLayoutEnabled)

  const inspectorUI =
    artboard && isInspectorEnabled ? (
      <Inspector
        isPage={artboard!.isPage}
        isSymbol={artboard!.isSymbolMaster}
        ready={!!inspectorData}
        error={inspectorDataErrors ?? inspectorError}
        errorReason={inspectorDataErrorReasons ?? inspectorErrorReason}
        inspector={inspector}
        artboardName={artboard.name}
        layer={
          selectedLayerId &&
          inspectorData &&
          inspectorData.layers[selectedLayerId as string]
        }
        shareIdentifier={share.identifier}
        currentVersion={currentVersion!}
        permanentArtboardShortId={artboard!.permanentArtboardShortId!}
      />
    ) : null

  return (
    <CenterContent>
      <ArtboardContext.Provider
        value={{
          isInspectorEnabled,
          isGridEnabled: !!isGridEnabled,
          toggleGrid,
          isLayoutEnabled: !!isLayoutEnabled,
          toggleLayout,
          zoomFactor: zoom?.factor ?? 1,
          gridSize: inspectorData?.grid?.size ?? 20,
          thickGridTimes: inspectorData?.grid?.thickGridTimes ?? 10,
          layoutSettings: inspectorData?.layout ?? defaultLayoutSettings,
        }}
      >
        <ComponentDescriptionContextProvider>
          <DocumentSidebarLayout
            title={artboard?.name}
            header={({
              toggleSidebarRight,
              isSidebarRightOpen,
              setSidebarLeftOpen,
            }) => (
              <>
                <NotEmbedded>
                  <DetailHeader
                    share={share}
                    artboard={artboard}
                    artboardLatestVersion={artboardIdAtLatestVersion!}
                    setSidebarLeftOpen={setSidebarLeftOpen}
                    toggleSidebarRight={toggleSidebarRight}
                    isSidebarRightOpen={isSidebarRightOpen}
                    isViewingLatestVersion={isViewingLatestVersion}
                    isPrototypePlayEnabled={isPrototypePlayEnabled}
                    userCanOpenInApp={
                      !!currentVersion?.document?.userCanOpenInApp
                    }
                    zoom={zoom}
                    setZoom={setZoom}
                  />
                </NotEmbedded>
                <IsEmbedded>
                  {!error && !loading && (
                    <EmbeddedDetailHeader
                      share={share}
                      artboard={artboard}
                      pagination={pagination}
                      currentZoom={zoom}
                      onZoomChanged={setZoom}
                      showPrototypePlayButton={
                        (artboard &&
                          artboard.isFlowHome &&
                          isPrototypePlayEnabled) ||
                        false
                      }
                    />
                  )}
                </IsEmbedded>
              </>
            )}
            sidebarLeft={
              <ShareWorkspaceSidebar
                workspaceIdentifier={share.workspace.identifier}
              />
            }
            sidebarRight={
              shouldRenderSidebarRight
                ? ({
                    toggleSidebarRight,
                  }: DocumentSidebarLayoutHeaderProps) => (
                    <DetailSidebar
                      share={share}
                      artboard={artboard!}
                      userCanInspect={share.userCanInspect}
                      toggleSidebar={toggleSidebarRight}
                      isViewingLatestRevision={isViewingLatestVersion}
                      inspectorUI={inspectorUI}
                    />
                  )
                : undefined
            }
          >
            <PageContent>
              {
                // <DismissibleCard /> will render a warning when there are
                // missing fonts in the artboard. If dismissed it wont render
                // again for any artboard in that particular document
              }
              <CustomFontsMessage
                userCanEdit={share.userCanEdit}
                shareIdentifier={share.identifier}
                hasMissingFonts={!!artboard?.hasMissingFonts}
              />
              {error ? (
                <>
                  <NotEmbedded>
                    <Redirect
                      to={routes.SHARE_VIEW.create({
                        shareID: share.identifier,
                      })}
                    />
                  </NotEmbedded>
                  <IsEmbedded>
                    <EmbeddedErrorPage for="artboard" />
                  </IsEmbedded>
                </>
              ) : (
                <ArtboardView
                  artboard={artboard}
                  pagination={pagination}
                  currentZoom={zoom}
                  onZoomChanged={setZoom}
                  inspector={inspector}
                  shareIdentifier={share.identifier}
                  permanentArtboardShortId={
                    artboard?.permanentArtboardShortId ?? undefined
                  }
                  permanentPageIdentifier={artboard?.page?.uuid || ''}
                />
              )}
            </PageContent>
            {!IS_EMBEDDED && !isTabletAndBigger && (
              <DocumentMobileToolbar
                zoom={zoom}
                setZoom={setZoom}
                isPrototypePlayEnabled={isPrototypePlayEnabled}
                shareIdentifier={share.identifier}
                items={[
                  'zoom',
                  artboard?.isFlowHome && 'prototype',
                  'add-annotation',
                ].filter(isMobileToolboxItem)}
                permanentArtboardShortId={
                  artboard?.permanentArtboardShortId || ''
                }
                isDraftAnnotationEnabled={placeDraftAnnotation}
                setPlaceDraftAnnotation={setPlacingDraftAnnotation}
              />
            )}
          </DocumentSidebarLayout>
        </ComponentDescriptionContextProvider>
      </ArtboardContext.Provider>
    </CenterContent>
  )
}
